'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "value"],
  _excluded2 = ["autoFocus", "ButtonContent", "buttonSize", "buttonUse", "className", "closeOnTargetLeave", "disabled", "error", "id", "ignoreAccents", "ignoreCase", "inputRef", "menuRenderer", "menuWidth", "noResultsText", "onChange", "onInputValueChange", "onOpenChange", "onSelectedOptionChange", "open", "placeholder", "readOnly", "resetValue", "searchable", "searchClassName", "searchPlaceholder", "searchPromptText", "tabIndex", "valueComponent", "valueRenderer"];
import Controllable from 'UIComponents/decorators/Controllable';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { createRef, Component } from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import Select from 'react-select-plus';
import devLogger from 'react-utils/devLogger';
import { callIfPossible, sequence } from 'UIComponents/core/Functions';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import { OptionOrGroupType, ValueType } from 'UIComponents/types/OptionTypes';
import { PLACEMENTS } from 'UIComponents/tooltip/PlacementConstants';
import UISearchInput from 'UIComponents/input/UISearchInput';
import lazyEval from 'UIComponents/utils/lazyEval';
import createLazyPropType from 'UIComponents/utils/propTypes/createLazyPropType';
import { hidden } from 'UIComponents/utils/propTypes/decorators';
import { MIN_MENU_WIDTH } from 'UIComponents/constants/Tether';
import UIButton from 'UIComponents/button/UIButton';
import refObject from 'UIComponents/utils/propTypes/refObject';
import { Consumer as FieldsetContextConsumer } from 'UIComponents/context/FieldsetContext';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIBackButton from 'UIComponents/nav/UIBackButton';
import DrilldownResultsItem from './DrilldownResultsItem';
import DrilldownSelectWithButtonAnchor from './DrilldownSelectWithButtonAnchor';
import { TINYMCE_PREVENT_BLUR_CLASSNAME } from 'data-token-picker/constants/TinyMCEHooks';
import { trimTree } from 'data-token-picker/utils/drilldown';
import { jsx as _jsx } from "react/jsx-runtime";
const {
  stripDiacritics
} = Select;
const SELECT_SIZE_OPTIONS = {
  default: '',
  small: 'drilldown-form__control--small'
};
const makeSearchList = options => {
  return options.map(option => {
    const {
        children,
        value
      } = option,
      rest = _objectWithoutPropertiesLoose(option, _excluded);
    return Object.assign({
      value: children && children.length > 0 ? undefined : value,
      options: children
    }, rest);
  });
};
const pathToSelection = (target, from = []) => {
  for (let i = 0; i < from.length; ++i) {
    if (from[i].value === target) {
      return [from];
    } else if (from[i].children) {
      const found = pathToSelection(target, from[i].children);
      if (found) {
        return [from, ...found];
      }
    }
  }
  return undefined;
};
const getCurrentOption = (target, from = []) => {
  for (let i = 0; i < from.length; ++i) {
    if (from[i].value === target) {
      return from[i];
    } else if (from[i].children) {
      const found = getCurrentOption(target, from[i].children);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};
const getResetOption = ({
  placeholder,
  resetValue
}, {
  resetOption
}) => {
  const newResetOption = {
    buttonText: lazyEval(placeholder),
    dropdownText: lazyEval(placeholder),
    value: resetValue
  };
  if (!(resetOption && resetOption.buttonText === newResetOption.buttonText && resetOption.value === newResetOption.value)) {
    return {
      resetOption: newResetOption
    };
  }
  return null;
};
const computeCurrentView = (props, state, updates = {}) => {
  const {
    options
  } = props;
  const {
    searchableOptions,
    value
  } = state;
  const inputValue = typeof updates.inputValue === 'string' ? updates.inputValue : state.inputValue;
  if (options) {
    let filteredOptions;
    let optionsStack;
    if (inputValue) {
      filteredOptions = trimTree(inputValue.toLowerCase(), searchableOptions);
    }
    if (value) {
      optionsStack = pathToSelection(value, options);
    } else {
      optionsStack = [options];
    }
    return {
      filteredOptions,
      optionsStack
    };
  }
  return null;
};
const initializeOptions = (props, state) => {
  if (props.options) {
    return Object.assign({
      searchableOptions: makeSearchList(props.options)
    }, computeCurrentView(props, state));
  }
  return null;
};
class DrilldownSelectCore extends Component {
  static getDerivedStateFromProps(props, state) {
    const resetOption = getResetOption(props, state);
    let openingState;
    let closingState;
    let prevOpen;
    const value = props.value;
    if (!state.prevOpen && props.open) {
      openingState = initializeOptions(props, state);
    } else if (state.prevOpen && !props.open) {
      closingState = {
        inputValue: '',
        initiallyOpen: false
      };
    }
    if (state.prevOpen !== props.open) {
      prevOpen = {
        prevOpen: props.open
      };
    }
    return Object.assign({
      value: value !== '' ? value : null
    }, resetOption, openingState, closingState, prevOpen);
  }
  constructor(props) {
    super(props);
    this.state = {
      controlWidth: 0,
      initiallyOpen: false,
      inputValue: '',
      optionsStack: [],
      prevOpen: false,
      resetOption: null,
      searchableOptions: null,
      value: ''
    };
    this.popoverRef = /*#__PURE__*/createRef();
    this.optionRendererProxy = option => {
      const {
        optionRenderer
      } = this.props;
      if (optionRenderer == null) {
        return this.defaultOptionRenderer(option);
      }
      return optionRenderer(option);
    };
    this.defaultValueRenderer = option => {
      const {
        itemComponent,
        valueComponent
      } = this.props;
      return /*#__PURE__*/_jsx(DrilldownResultsItem, {
        "aria-invalid": option.invalid,
        itemComponent: valueComponent || itemComponent,
        option: Object.assign({}, option, {
          help: undefined
        }),
        _useButton: false,
        _useDropdownText: false
      }, option.value);
    };
    this.filterOptionsProxy = options => {
      return options;
    };
    this.handleChange = option => {
      // react-select-plus gives us an option object or null, which we pass to
      // onSelectedOptionChange, but our onChange handler expects just the new value.
      const {
        onOpenChange,
        onChange,
        onSelectedOptionChange
      } = this.props;
      const {
        value
      } = this.state;
      callIfPossible(onSelectedOptionChange, SyntheticEvent(option));
      let newVal = null;
      if (Array.isArray(option)) {
        newVal = option.reduce((acc, item) => {
          if (item) {
            const {
              value: itemValue
            } = item;
            const i = acc.indexOf(itemValue);
            if (i > -1) {
              acc.splice(i, 1);
            } else {
              acc.push(itemValue);
            }
          }
          return acc;
        }, []);
      } else if (option) {
        newVal = option.value;
      }
      if (option.children) {
        onOpenChange(SyntheticEvent(true));
        const {
          optionsStack
        } = this.state;
        this.setState({
          optionsStack: [...optionsStack, option.children]
        });
      } else {
        onOpenChange(SyntheticEvent(false));
        if (newVal !== value) {
          this.setState({
            value: newVal
          });
          onChange(SyntheticEvent(option));
        }
      }
    };
    this.handleInputKeyDown = evt => {
      const {
        onInputKeyDown
      } = this.props;
      callIfPossible(onInputKeyDown, evt);
      if (evt.defaultPrevented) {
        return;
      }
    };
    this.triggerOpen = () => {
      const {
        onOpenChange
      } = this.props;
      onOpenChange(SyntheticEvent(true));
    };
    this.handleOpenChange = evt => {
      const {
        onOpenChange
      } = this.props;
      callIfPossible(onOpenChange, evt);
    };
    this.handleInputChange = str => {
      const {
        onInputChange
      } = this.props;
      callIfPossible(onInputChange, str);
      this.setState({
        inputValue: str
      });
      return str;
    };
    this.handleInputValueChange = evt => {
      const {
        onInputValueChange
      } = this.props;
      callIfPossible(onInputValueChange, evt);
      this.setState(Object.assign({
        inputValue: evt.target.value
      }, computeCurrentView(this.props, this.state, {
        inputValue: evt.target.value
      })));
    };
    this.focus = () => {
      try {
        this._anchorRef.focus();
      } catch (e) {
        /* ¯\_(ツ)_/¯ */
      }
    };
    this.handleAutosize = () => {
      if (this.popoverRef.current) this.popoverRef.current.reposition();
    };
    this.anchorRefCallback = anchorRef => {
      const {
        controlWidth,
        target
      } = this.state;
      this._anchorRef = anchorRef;
      const newTarget = findDOMNode(this._anchorRef);
      const newControlWidth = this.computeControlWidth(newTarget);
      if (target !== newTarget || controlWidth !== newControlWidth) {
        this.setState({
          controlWidth: newControlWidth,
          target: newTarget
        });
      }
    };
    this.selectRefCallback = () => {
      // Hackily override Select's selectValue and handleMouseDownOnMenu to support "Load more"
      const internalSelect = this.getInternalSelect();
      if (!internalSelect) return;
      internalSelect.selectValue = this.selectValueProxy.bind(this, internalSelect.selectValue);
    };
    this.getInternalSelect = () => {
      // Return our react-select Select instance, or null
      if (!this._anchorRef) {
        return null;
      }
      return this._anchorRef.getInternalSelect();
    };
    const resetOption = getResetOption(props, this.state);
    this.state = Object.assign({}, this.state, resetOption, {
      initiallyOpen: props.open,
      prevOpen: props.open
    });
  }
  componentDidUpdate(prevProps) {
    const controlEl = findDOMNode(this._anchorRef);
    if (!controlEl) {
      return;
    }
    const controlWidth = this.computeControlWidth(controlEl);
    if (controlWidth !== this.state.controlWidth) {
      this.setState({
        controlWidth
      });
    }
    if (!prevProps.open && this.props.open) {
      this.focus();
    }
  }
  getOptions() {
    const {
      options
    } = this.props;
    const {
      optionsStack,
      inputValue,
      filteredOptions,
      value
    } = this.state;
    let currentOptions = options;
    if (inputValue && filteredOptions) {
      currentOptions = filteredOptions;
    } else if (optionsStack.length) {
      currentOptions = optionsStack[optionsStack.length - 1];
    }
    let currentOption;
    if (currentOptions && !currentOptions.some(entry => entry.value === value)) {
      const currentSelection = getCurrentOption(value, options);
      if (currentSelection) {
        currentOption = {
          disabled: true,
          hide: true,
          text: currentSelection.text,
          value: currentSelection.value
        };
      }
    }
    return currentOption ? [...currentOptions, currentOption] : currentOptions;
  }
  defaultOptionRenderer(option) {
    if (option.hide) {
      return null;
    }
    const {
      itemComponent,
      optionComponent
    } = this.props;
    return /*#__PURE__*/_jsx(DrilldownResultsItem, {
      itemComponent: optionComponent || itemComponent,
      option: option,
      style: option.dropdownStyle
    });
  }
  // Extracted to a function so it can be stubbed out in tests
  getDefaultFilterOptionsFunc() {
    return Select.defaultProps.filterOptions;
  }
  selectValueProxy(selectValue, selectedOption) {
    selectValue(selectedOption);
  }
  getMenuWidth() {
    const {
      menuWidth
    } = this.props;
    if (menuWidth === 'auto') {
      return '';
    }
    if (typeof menuWidth === 'number') {
      return menuWidth;
    }
    return Math.max(this.state.controlWidth, MIN_MENU_WIDTH);
  }
  computeControlWidth(controlEl) {
    const {
      open
    } = this.props;
    if (!controlEl || !open) {
      return 0;
    }
    const exactWidth = controlEl.getBoundingClientRect().width;
    const roundedWidth = window.chrome ? Math.floor(exactWidth) : exactWidth;
    return roundedWidth - 2; // account for 1px border added by UIPopover
  }
  getNoResultsText({
    noResultsText
  }) {
    if (noResultsText != null) return lazyEval(noResultsText);
    const {
      inputValue
    } = this.state;
    return inputValue ? I18n.text('data-token-picker.drilldownSelect.noResults') : I18n.text('data-token-picker.drilldownSelect.typeToSearch');
  }
  renderBackButton() {
    const {
      rootBreadcrumb
    } = this.props;
    const {
      inputValue,
      optionsStack
    } = this.state;
    if (inputValue || optionsStack.length <= 1) {
      return null;
    }
    let backButton;
    if (rootBreadcrumb) {
      backButton = /*#__PURE__*/_jsx(UIBackButton, {
        className: classNames('display-inline', TINYMCE_PREVENT_BLUR_CLASSNAME),
        children: /*#__PURE__*/_jsx(UIButton, {
          onClick: () => {
            optionsStack.pop();
            this.setState({
              optionsStack
            });
          },
          use: "link",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "data-token-picker.drilldownSelect.backBreadcrumbOption",
            options: {
              breadcrumbPath: rootBreadcrumb
            }
          })
        })
      });
    } else {
      backButton = /*#__PURE__*/_jsx(UIButton, {
        className: TINYMCE_PREVENT_BLUR_CLASSNAME,
        onClick: () => {
          optionsStack.pop();
          this.setState({
            optionsStack
          });
        },
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "data-token-picker.drilldownSelect.backOption"
        })
      });
    }
    return /*#__PURE__*/_jsx("div", {
      className: "drilldown__header-container",
      children: backButton
    });
  }
  render() {
    const _this$props = this.props,
      {
        autoFocus,
        ButtonContent,
        buttonSize,
        buttonUse,
        className,
        closeOnTargetLeave,
        disabled,
        error,
        id,
        ignoreAccents,
        ignoreCase,
        inputRef,
        menuRenderer,
        menuWidth,
        noResultsText,
        onOpenChange,
        open,
        placeholder,
        readOnly,
        resetValue,
        searchable,
        searchClassName,
        searchPlaceholder,
        searchPromptText,
        tabIndex,
        valueRenderer
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    const {
      initiallyOpen,
      resetOption,
      value
    } = this.state;

    // A null or undefined value should be considered equivalent to resetValue
    const computedValue = value == null ? resetValue : value;
    if (rest.autofocus) {
      devLogger.warn({
        message: "UISelect: The autofocus prop is deprecated in favor of autoFocus to match React's autoFocus prop",
        key: 'UISelect: autofocus'
      });
    }

    // We need to attach two ref callbacks to the combined anchor.
    this._refSequence = this._refSequence || sequence(this.anchorRefCallback, this.selectRefCallback);
    const options = this.getOptions();
    return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
      children: ({
        disabled: fieldDisabled,
        size: fieldSize
      }) => {
        const computedDisabled = disabled || fieldDisabled;
        // Props that are used regardless of anchor type
        const commonProps = Object.assign({}, rest, {
          backspaceRemoves: false,
          clearable: false,
          disabled: computedDisabled,
          filterOptions: this.filterOptionsProxy,
          id,
          ignoreAccents,
          ignoreCase,
          inputProps: {},
          isOpen: open && !readOnly && !!this.state.target,
          labelKey: 'text',
          menuRenderer,
          noResultsText: this.getNoResultsText({
            noResultsText
          }),
          onChange: this.handleChange,
          onInputChange: this.handleInputChange,
          onInputKeyDown: this.handleInputKeyDown,
          openAfterFocus: true,
          optionRenderer: this.optionRendererProxy,
          options,
          placeholder: lazyEval(placeholder),
          readOnly,
          ref: this.anchorRefCallback,
          renderInvalidValues: true,
          required: false,
          resetValue,
          searchable,
          searchPromptText: lazyEval(searchPromptText),
          tabIndex,
          tabSelectsValue: false,
          value: computedValue,
          valueComponent: Select.defaultProps.valueComponent,
          valueRenderer: valueRenderer || this.defaultValueRenderer
        });
        const simpleAnchorClassName = classNames(className, SELECT_SIZE_OPTIONS[fieldSize], error && 'drilldown-form__control--error');
        return /*#__PURE__*/_jsx(DrilldownSelectWithButtonAnchor, Object.assign({}, commonProps, {
          autoComplete: "off",
          autoFocus: autoFocus,
          buttonSize: buttonSize,
          ButtonContent: ButtonContent,
          buttonUse: buttonUse,
          className: simpleAnchorClassName,
          closeOnTargetLeave: closeOnTargetLeave,
          error: error,
          initiallyOpen: initiallyOpen,
          inputRef: inputRef,
          menuWidth: menuWidth,
          onInputValueChange: this.handleInputValueChange,
          onOpenChange: onOpenChange,
          resetOption: resetOption,
          searchClassName: searchClassName,
          searchPlaceholder: lazyEval(searchPlaceholder),
          selectComponent: Select,
          selectRef: this.selectRefCallback,
          minimumSearchCount: 0,
          dropdownHeader: this.renderBackButton()
        }));
      }
    });
  }
}
DrilldownSelectCore.propTypes = {
  /**
   * Auto-focuses on the select input on render.
   */
  autoFocus: PropTypes.bool,
  /**
   * Provide a custom button component to render if `anchorType="button"`. The
   * component is passed a `buttonText` prop.
   */
  ButtonContent: DrilldownSelectWithButtonAnchor.propTypes.ButtonContent,
  /**
   * Sets the size of the button used for the anchor of the component,
   * corresponding to `UIButton` sizes. Only used when `anchorType="button"`.
   */
  buttonSize: UIButton.propTypes.size,
  /**
   * Sets the visual styles of the button used for the anchor of the component,
   * corresponding to `UIButton` uses. Only used when `anchorType="button"`.
   */
  buttonUse: UIButton.propTypes.use,
  className: PropTypes.string,
  /**
   * Provides an affordance to clear the current value of the select. When used
   * with `anchorType="combined"` an "X" is shown on the input, otherwise the
   * placeholder value is added as an option to clear the select.
   */
  clearable: PropTypes.bool.isRequired,
  /**
   * Automatically triggers a close event for the option menu if the select
   * anchor is scrolled out of view.
   */
  closeOnTargetLeave: PropTypes.bool,
  /**
   * Determines whether the option menu is open by default. The `open` prop takes
   * precedence over this if used in a controlled manner.
   */
  defaultOpen: PropTypes.bool,
  /**
   * The `value` to use for initial selection of an option.
   */
  defaultValue: ValueType,
  /**
   * Determines if the select input is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * A custom className to pass to the container of the dropdown option menu.
   */
  dropdownClassName: PropTypes.string,
  /**
   * Content that will be shown at the bottom of the select dropdown; most
   * commonly used for an “Add option” button that opens a popover with input
   * fields to gather more information for the option creation.
   */
  dropdownFooter: PropTypes.node,
  /**
   * When `true`, shows an error affordance around the select input.
   * `UIFormControl` should be used around the select to add inline error
   * messaging.
   */
  error: PropTypes.bool,
  /**
   * Provide custom functionality to replace the method used to filter options.
   */
  filterOptions: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /**
   * An id that is passed to the computed anchor element. Works with
   * `anchorType="button"` or `anchorType="combined"`.
   */
  id: PropTypes.string,
  /**
   * Ignores accented characters and replaces them with the base character.
   */
  ignoreAccents: hidden(PropTypes.bool),
  /**
   * When enabled, search strings are matched to options case-insensitively.
   */
  ignoreCase: hidden(PropTypes.bool),
  /**
   * Provide a custom input component to use as the anchor component. Only used
   * when `anchorType="input"`.
   */
  inputComponent: PropTypes.elementType,
  /**
   * Set a `ref` to the underlying input component.
   */
  inputRef: refObject,
  /**
   * Controls the value of the underlying input. Only used when
   * `anchorType="input"`.
   */
  inputValue: PropTypes.string,
  /**
   * The component used to render an option in both the dropdown list and the
   * selected value box. Receives the option object as a prop, along with
   * several other props that should get routed to the root element of the
   * `itemComponent`. The `optionComponent` prop overrides this prop's value
   * for rendering options in the dropdown list, and the `valueComponent` prop
   * overrides this prop's value for rendering options in the selected value box.
   */
  itemComponent: DrilldownResultsItem.propTypes.itemComponent,
  /**
   * Provide a custom function to render the options menu, typically used for
   * virtualized menu rendering. Ordinarily set to `UIComponents/input/utils/virtualizedMenuRenderer`, a react-virtualized function which
   * accepts a number of named parameters.
   */
  menuRenderer: PropTypes.func,
  /**
   * A style object to be applied to the options menu.
   */
  menuStyle: PropTypes.object,
  /**
   * Sets a custom width on the dropdown menu.
   */
  menuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  /**
   * Text shown when there are no results for given search term within the
   * select.
   */
  noResultsText: createLazyPropType(PropTypes.node),
  /**
   * Provides a custom function that is called whenever the value of the select
   * changes.
   */
  onChange: PropTypes.func,
  /**
   * Triggered whenever a key is pressed when there’s focus on an open select
   * (including arrow keys, shift, etc).
   */
  onInputKeyDown: PropTypes.func,
  /**
   * Triggered whenever the search value has changed or an option has been
   * selected (even if it’s the same option).
   */
  onInputChange: PropTypes.func,
  /**
   * Triggered whenever the search value has changed.
   */
  onInputValueChange: PropTypes.func,
  /**
   * A function to call whenever the select’s menu open state changes.
   */
  onOpenChange: PropTypes.func,
  /**
   * A function to call whenever a value is pasted into the select. The pasted
   * value can be accessed from the clipboardData field on the event object.
   */
  onPaste: PropTypes.func,
  /**
   * Triggered whenever an option is selected. This event fires even if the
   * same option is selected, unlike `onChange`.
   */
  onSelectedOptionChange: PropTypes.func,
  /**
   * Controls the open state of the option menu.
   */
  open: PropTypes.bool,
  /**
   * The component used to render an option in the dropdown list. Receives the
   * option object as a prop, along with several other props that should get
   * routed to the root element of the `optionComponent`. Defaults to the value
   * of the `itemComponent` prop.
   */
  optionComponent: DrilldownResultsItem.propTypes.itemComponent,
  /**
   * The options from which the user may select from. In most cases, each
   * option object must have a `text` key, which controls the display text
   * and is used in search, and a `value` key.
   */
  options: PropTypes.arrayOf(OptionOrGroupType),
  /**
   * Provide a custom function that is responsible for rendering each option.
   * Receives an option object as its arguments.
   */
  optionRenderer: PropTypes.func,
  /**
   * The placeholder text in the select. This also becomes the text that allows
   * a user to clear the current selected option when used with clearable
   * `anchorType="button"|"input"`.
   */
  placeholder: createLazyPropType(PropTypes.node).isRequired,
  /**
   * Controls the placement of the option menu popover.
   */
  placement: PropTypes.oneOf(PLACEMENTS),
  /**
   * Determines if the select input is read-only or editable.
   */
  readOnly: PropTypes.bool,
  /**
   * The value set when the user clears the select input. Only possible when
   * `clearable={true}`.
   */
  resetValue: ValueType,
  /**
   * A custom className to pass to the container of the list of displayed
   * options inside of the popover.
   */
  resultsClassName: PropTypes.string,
  /**
   * Label for root category when displaying a destination for the back button.
   */
  rootBreadcrumb: PropTypes.string,
  /**
   * Whether or not the search input is visible. Note that in the current
   * behavior, the select still always filters when typing in the input.
   */
  searchable: PropTypes.bool.isRequired,
  /**
   * ClassName passed to the search input within the select.
   */
  searchClassName: PropTypes.string,
  /**
   * Placeholder text in the search input within the select.
   */
  searchPlaceholder: createLazyPropType(PropTypes.string).isRequired,
  /**
   * Text to display when options have not been fetched yet by `loadOptions`.
   */
  searchPromptText: createLazyPropType(PropTypes.string).isRequired,
  /**
   * Sets a custom value for `tabIndex` on the rendered anchor element.
   */
  tabIndex: PropTypes.number,
  /**
   * Dot-delineated value to current selection within the options tree.
   */
  value: PropTypes.string,
  /**
   * The component used to render the options in the selected value box.
   * Receives the option object as a prop, along with several other props that
   * should get routed to the root element of the `valueComponent`. Defaults to
   * the value of the `itemComponent` prop.
   */
  valueComponent: DrilldownResultsItem.propTypes.itemComponent,
  /**
   * Provide a custom function that is responsible for rendering the current
   * value of the select, shown in the anchor input or button.
   */
  valueRenderer: PropTypes.func
};
DrilldownSelectCore.defaultProps = {
  buttonSize: 'default',
  buttonUse: 'form',
  clearable: false,
  open: false,
  ignoreAccents: true,
  ignoreCase: true,
  inputComponent: UISearchInput,
  itemComponent: 'span',
  placeholder: () => I18n.text('data-token-picker.drilldownSelect.placeholder'),
  resetValue: '',
  searchable: true,
  searchPlaceholder: () => I18n.text('data-token-picker.drilldownSelect.placeholder'),
  searchPromptText: () => I18n.text('data-token-picker.drilldownSelect.typeToSearch'),
  value: ''
};
DrilldownSelectCore.displayName = 'UISelect';
const DrilldownSelect = Controllable(DrilldownSelectCore, ['open']);
DrilldownSelect.stripDiacritics = stripDiacritics;
export default DrilldownSelect;